import React, { useCallback, useState } from 'react';
import {
  Box,
  FormProvider,
  Preloader,
  Text,
} from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';
import { WarnModal } from '@/components/elements';

import useResetPasswordForm, { ResetPasswordField, ResetPasswordForm } from '../hooks/useResetPasswordForm';
import useResetPasswordQuery from '../queries/useResetPassword.query';

import ResetPasswordFields from './ResetPasswordFields';

interface ResetPasswordProps {
  linkId: string;
  code: string;
  onSuccess: () => void;
}

const EnterNewPassword: React.FC<ResetPasswordProps> = ({ linkId, code, onSuccess }) => {
  const { methods } = useResetPasswordForm();
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const { resetPasswordQuery, isResetPasswordLoading } = useResetPasswordQuery();

  const handleSubmit = useCallback(async (formValues: ResetPasswordForm) => {
    try {
      await resetPasswordQuery({
        linkId,
        body: {
          code,
          password: formValues[ResetPasswordField.newPassword],
        },
      });
      onSuccess();
    } catch {
      setIsErrorModalVisible(true);
    }
  }, [resetPasswordQuery, onSuccess, linkId, code]);

  return (
    <>
      <Box data-testid="reset-password_enter-new-password">
        <Text
          size="2xl"
          textAlign="center"
          weight={700}
          data-testid="reset-password_title"
        >
          Reset Password
        </Text>

        <Text
          margin={{ top: 'spacing8' }}
          size="large"
          textAlign="center"
          data-testid="reset-password_subtitle"
        >
          Please choose your new password.
        </Text>

        <Box margin={{ top: 'spacing16' }}>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)} data-testid="reset-password_form">
              <ResetPasswordFields />

              <Box margin={{ top: 'spacing16' }}>
                <AppButton
                  testId="reset-password_submit-btn"
                  type="submit"
                  width="100%"
                  size="L"
                  disabled={isResetPasswordLoading}
                >
                  {isResetPasswordLoading
                    ? <Preloader color="white" data-testid="reset-password_submit-loader" />
                    : 'Reset Password'}
                </AppButton>
              </Box>
            </form>
          </FormProvider>
        </Box>
      </Box>

      {isErrorModalVisible && (
        <WarnModal
          testId="reset-password_error"
          visible={isErrorModalVisible}
          onSetVisible={setIsErrorModalVisible}
          header="The server encountered an error processing the request"
          helptext={`
            It does not appear to have affected your data, but we cannot process your request.
            Our technical stuff has been automatically and will be looking into this with the utmost urgency.
          `}
          buttonText="Close"
          onSubmit={methods.handleSubmit(handleSubmit)}
          submitButtonText="Try again"
          isAwaitMode
          disableAutomaticClosing
        />
      )}
    </>
  );
};

export default EnterNewPassword;
